import { create } from "zustand";
import type { Notification } from "~/types/notification";

/** 直前にトーストを表示してから次のトーストを表示するまでの最短間隔（ミリ秒）。この間に届いた通知は表示せず破棄する */
const TOAST_COOLDOWN_MS = 10_000;

type NotificationState = {
  notifications: Notification[];
  unreadCount: number;
  toastQueue: Notification[];
  /** 直近でトーストの表示を開始した時刻（ms）。10秒のクールダウン判定の起点 */
  lastShownAt: number | null;
};

type NotificationActions = {
  addNotification: (notification: Notification) => void;
  markAsRead: (id: string) => void;
  clearAll: () => void;
  /** 既に表示中/表示待ちがある、またはクールダウン中は表示せず破棄する（後から追いかけて表示することはしない） */
  enqueueToast: (notification: Notification) => void;
  dequeueToast: () => void;
  clearToastQueue: () => void;
  /** トーストの表示開始を記録する（クールダウン判定の起点を更新） */
  markToastShown: () => void;
};

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  toastQueue: [],
  lastShownAt: null,
};

const isToastCooldownActive = (lastShownAt: number | null) =>
  lastShownAt !== null && Date.now() - lastShownAt < TOAST_COOLDOWN_MS;

export const useNotificationStore = create<
  NotificationState & NotificationActions
>()((set) => ({
  ...initialState,

  addNotification: (notification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: notification.isRead
        ? state.unreadCount
        : state.unreadCount + 1,
    })),

  markAsRead: (id) =>
    set((state) => {
      const target = state.notifications.find((n) => n.id === id);
      if (!target || target.isRead) return state;
      return {
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, isRead: true } : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    }),

  clearAll: () =>
    set({
      notifications: [],
      unreadCount: 0,
    }),

  enqueueToast: (notification) =>
    set((state) => {
      // 既に表示中/表示待ちがある、またはクールダウン中の通知は破棄する（後から追いかけて表示しない）。
      // toastQueue の同期的な参照により、同一 tick で複数回呼ばれても1件目以降は即座に弾かれる
      if (state.toastQueue.length > 0) return state;
      if (isToastCooldownActive(state.lastShownAt)) return state;
      return { toastQueue: [notification] };
    }),

  dequeueToast: () =>
    set((state) => ({
      toastQueue: state.toastQueue.slice(1),
    })),

  clearToastQueue: () => set({ toastQueue: [] }),

  markToastShown: () => set({ lastShownAt: Date.now() }),
}));
